import type { CSSProperties } from 'react';

export const styles: { [key: string]: CSSProperties } = {
  container: { maxWidth: '500px', margin: '40px auto', padding: '24px', border: '1px solid #e0e0e0', borderRadius: '12px', fontFamily: 'Arial, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', backgroundColor: '#fff' },
  title: { fontSize: '18px', fontWeight: 'bold', margin: '0 0 20px 0', color: '#111', textAlign: 'center' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontWeight: 'bold', fontSize: '14px', color: '#333' },
  searchRow: { display: 'flex', gap: '8px' },
  input: { flex: 1, padding: '10px 12px', fontSize: '15px', borderRadius: '6px', border: '1px solid #ccc', outline: 'none' },
  searchButton: { padding: '10px 18px', backgroundColor: '#1890ff', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' },
  resultCard: { marginTop: '24px', padding: '16px', border: '1px solid #f0f0f0', borderRadius: '8px', backgroundColor: '#fafafa' },
  cardHeader: { margin: '0 0 14px 0', fontSize: '15px', color: '#555', borderBottom: '1px solid #f0f0f0', paddingBottom: '6px' },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  infoLabel: { fontSize: '14px', color: '#666', fontWeight: '500' },
  infoValue: { fontSize: '14px', color: '#111', fontWeight: 'bold' },
  statusBadge: { padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', border: '1px solid' },
  actionButton: { marginTop: '14px', width: '100%', padding: '12px', fontSize: '14px', color: '#fff', backgroundColor: '#52c41a', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', transition: 'background-color 0.2s' },
  errorText: { color: '#ff4d4f', fontSize: '14px', marginTop: '12px', fontWeight: '500', textAlign: 'center' },
  successText: { color: '#52c41a', fontSize: '14px', marginTop: '12px', fontWeight: '500', textAlign: 'center' },
  warningText: { color: '#CFB313', fontSize: '14px', marginTop: '12px', fontWeight: '500', textAlign: 'center' },
};

/** Visual treatment (background/text/border) for the status badge, keyed off the raw Okta status string. */
export function getStatusBadgeStyle(status: string): CSSProperties {
  const s = status.toLowerCase();
  const isCritical = s === 'suspended' || s === 'deprovisioned';
  const isCriticalOrLocked = isCritical || s === 'locked_out';
  const isPending = s === 'recovery' || s === 'provisioned';

  return {
    ...styles.statusBadge,
    backgroundColor: isCritical ? '#fff1f0' : '#f6ffed',
    color: isCriticalOrLocked ? '#cf1322' : isPending ? '#ffa500' : s === 'password_expired' ? '#CFB313' : '#389e0d',
    borderColor: isCritical ? '#ffa39e' : '#b7eb8f',
  };
}

/** Human-readable label for the status badge; some raw Okta statuses get a friendlier phrase. */
export function getStatusLabel(status: string): string {
  switch (status.toUpperCase()) {
    case 'DEPROVISIONED':
      return 'DEACTIVATED';
    case 'RECOVERY':
      return 'Password reset. User is now in one-time password mode.';
    case 'PROVISIONED':
      return 'Pending user action. User password selection required.';
    default:
      return status.toUpperCase();
  }
}

/** Picks the message style (error/warning/success) based on the content of the actionSuccess string. */
export function getMessageStyle(message: string): CSSProperties {
  if (message.includes('needs to be reactivated')) return styles.errorText;
  if (message.includes('password is expired')) return styles.warningText;
  return styles.successText;
}
